import * as React from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { ProductPrice, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { ICartState } from '@msdyn365-commerce/global-state';
import { AddToCartComponent, IAddToCartToastResources } from '../../dlvry-buybox/common/add-to-cart.component';
import { ProductSearchResultExt, SimpleProductExt } from '../../../shared-utils/types';
import CartCount from './cart-count';
import PQueue from 'p-queue';
import { BuyValidationResult } from '../../../themes/dlvry-adventureworks/views/components/product.component';
import { IDlvrySearchResources } from '../dlvry-search.props.autogenerated';
import QuantityBreakoffSelector from '../../../components/common/quantity-breakoff-selector';
import QuantityInput from '../../../components/common/quantity-input';
import { getNonBreakoffUOM, getTotalConversionText } from '../../../shared-utils/unit-conversion-utils';

interface IBuySectionProps {
    context: ICoreContext;
    id: string;
    typeName: string;
    resources: IDlvrySearchResources;
    productRecordId: number;
    simpleProduct: SimpleProductExt | undefined;
    productPrice: ProductPrice | undefined;
    cart: ICartState;
    cartQueue: PQueue | undefined;
    inCartTooltipText: string | undefined;
    unitConversions: UnitOfMeasureConversion[] | undefined;
    onBuyValidate: (product: ProductSearchResultExt, quantity: number) => Promise<BuyValidationResult>;

    quantity: number,
    breakoff: boolean,
    onQuantityChange: (productRecordId: number, quantity: number) => void,
    onBreakoffChange: (productRecordId: number, breakoff: boolean) => void
}

const BuySection = (props: IBuySectionProps) => {
    const {
        id,
        typeName,
        context,
        resources,
        productRecordId,
        simpleProduct,
        productPrice,
        cart,
        cartQueue,
        inCartTooltipText,
        quantity,
        breakoff,
        onQuantityChange,
        onBreakoffChange
    } = props;
    const [forceUpdate , setForceUpdate] = React.useState(0);

    // Prevents clicks inside the <a> element from navigating if on the quantity elements
    const onContainerClick = React.useCallback((e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.dlvry-switch') || (e.target as HTMLElement).closest('.quantity-breakoff-selector')) {
            // No need to prevent default if the click is on the switch and will in fact break the switch
            return;
        }

        e.preventDefault();
        e.stopPropagation();
    }, []);

    const toastStrings: IAddToCartToastResources = {
        errorHeader: resources.toastErrorHeader,
        productsAddedToCart: resources.productsAddedToCart,
        productsRemovedFromCart: resources.productsRemovedFromCart,
        productsUpdatedInCart: resources.productsUpdatedInCart,
        goToCart: resources.toastGoToCart,
        noAccessToProduct: resources.toastNoAccessToProduct
    };

    const productSearchResult: ProductSearchResultExt = {
        RecordId: productRecordId,
        Brand: simpleProduct?.Brand,
        Category: simpleProduct?.Category,
        CategoryPaths: simpleProduct?.CategoryPaths,
        ItemId: simpleProduct?.ItemId,
        Name: simpleProduct?.Name,
        Price: productPrice?.CustomerContextualPrice || 0
    };

    const totalConversionText = getTotalConversionText(
        context,
        simpleProduct,
        props.unitConversions,
        quantity,
        breakoff
    );

    return (
        <div className='ms-search__buy-section' draggable onDragStart={e => e.preventDefault()} onClick={onContainerClick}>
            <QuantityBreakoffSelector
                context={context}
                product={simpleProduct}
                quantity={quantity}
                breakoff={breakoff}
                unitConversions={props.unitConversions}
                onQuantityChange={quantity => onQuantityChange(productRecordId, quantity)}
                onBreakoffChange={breakoff => onBreakoffChange(productRecordId, breakoff)}
            />

            <QuantityInput
                context={context}
                product={simpleProduct}
                quantity={quantity}
                breakoff={breakoff}
                onQuantityChange={quantity => onQuantityChange(productRecordId, quantity)}
                nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, props.unitConversions)}
            />

            {totalConversionText && (
                <span className={`ms-search__total-conversion-text`}>
                    =
                </span>
            )}

            <span className={`ms-search__total-conversion-text`}>
                {totalConversionText}
            </span>

            {simpleProduct && (
                <div className='ms-search__buy-section__right'>
                    <CartCount
                        key={forceUpdate}
                        locale={context.request.locale}
                        cart={cart}
                        product={simpleProduct}
                        productId={productRecordId}
                        inCartTooltipText={inCartTooltipText}
                    />
                    <AddToCartComponent
                        id={id}
                        context={context}
                        typeName={typeName}
                        quantity={quantity}
                        breakoff={breakoff}
                        onUpdateQuantity={quantity => onQuantityChange(productRecordId, quantity)}
                        data={{
                            cart,
                            product: simpleProduct,
                            price: productPrice,
                            productSearch: productSearchResult }}
                        cartQueue={cartQueue}
                        addToCartText={resources.addToCartButtonText}
                        removeFromCartText='WIP Fjern fra kurv'
                        updateCartText='WIP Oppdater'
                        expiredText={resources.expiredText}
                        toastStrings={toastStrings}
                        onBuyValidate={props.onBuyValidate}
                        onAdd={async () => { setForceUpdate(forceUpdate + 1); }}
                    />
                </div>
            )}
        </div>
    );
};

export default BuySection;
